// FE-SCRM-057 — MarkSentModal.
//
// Records that a proposal was sent to a specific contact at a
// specific time. The BE auto-appends a ``proposal_delivered``
// SalesActivity (per BE-SCRM-029) which BE-SCRM-023's
// auto-Touchpoint rule then turns into a contact event; the
// invalidation chain in :func:`useMarkProposalSentMutation` brings
// it into the rail without a refetch.
//
// Recipient is a typeahead over the opportunity's prospect's
// contacts (the BE enforces this same scope — sending a proposal
// "to" a contact on a different prospect is rejected).

import { useState } from "react";

import { Modal } from "@/design-system";

import { useContactsListQuery } from "../Contacts/hooks";

import type { MarkSentInput, Proposal } from "./types";


export interface MarkSentModalProps {
  proposal: Proposal;
  prospectId: string;
  isPending: boolean;
  onSubmit: (input: MarkSentInput) => void;
  onClose: () => void;
}


export function MarkSentModal({
  proposal, prospectId, isPending, onSubmit, onClose,
}: MarkSentModalProps) {
  const [recipientId, setRecipientId] = useState<string>("");
  const [sentAt, setSentAt] = useState<string>(_localNowIso());
  const [error, setError] = useState<string | null>(null);

  const contactsQuery = useContactsListQuery({
    prospect_id: [prospectId],
    include_archived: false,
    page_size: 100,
  });
  const contacts = contactsQuery.data?.rows ?? [];

  function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!recipientId) {
      setError("Pick a recipient contact.");
      return;
    }
    setError(null);
    const input: MarkSentInput = {
      recipient_contact_id: recipientId,
    };
    if (sentAt) input.sent_at = new Date(sentAt).toISOString();
    onSubmit(input);
  }

  return (
    <Modal
      open
      onClose={onClose}
      title={`Mark v${proposal.version} as sent`}
      size="sm"
      footer={
        <>
          <button
            type="button"
            data-testid="proposal-mark-sent-cancel"
            onClick={onClose}
            disabled={isPending}
            className={
              "rounded-md border border-border-subtle px-3 py-1.5 text-sm "
              + "text-text-primary hover:bg-recessed disabled:opacity-60"
            }
          >
            Cancel
          </button>
          <button
            type="submit"
            form="proposal-mark-sent-form"
            data-testid="proposal-mark-sent-submit"
            disabled={isPending}
            className={
              "rounded-md bg-teal px-3 py-1.5 text-sm font-medium "
              + "text-text-inverse hover:opacity-90 disabled:opacity-60"
            }
          >
            {isPending ? "Saving…" : "Mark sent"}
          </button>
        </>
      }
    >
      <form
        id="proposal-mark-sent-form"
        data-testid="proposal-mark-sent-modal"
        onSubmit={handleSubmit}
        className="flex flex-col gap-3"
      >
        {error && (
          <div
            role="alert"
            data-testid="proposal-mark-sent-error"
            className="rounded-md bg-ember/15 px-3 py-2 text-sm text-ember-ink"
          >
            {error}
          </div>
        )}

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">Recipient</span>
          <select
            data-testid="proposal-mark-sent-recipient"
            value={recipientId}
            onChange={(e) => setRecipientId(e.target.value)}
            className={
              "rounded-md border border-border-subtle bg-raised px-2 "
              + "py-1.5 text-sm"
            }
          >
            <option value="">— Pick a contact —</option>
            {contacts.map((c) => (
              <option key={c.contact_id} value={c.contact_id}>
                {c.display_name}
                {c.title ? ` · ${c.title}` : ""}
              </option>
            ))}
          </select>
          {contactsQuery.isLoading ? (
            <span className="text-xs text-text-tertiary">
              Loading contacts…
            </span>
          ) : contacts.length === 0 ? (
            <span className="text-xs text-text-tertiary">
              This prospect has no contacts on file yet. Add one from
              the Prospect detail page first.
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-sm">
          <span className="font-medium text-text-primary">Sent at</span>
          <input
            type="datetime-local"
            data-testid="proposal-mark-sent-at"
            value={sentAt}
            onChange={(e) => setSentAt(e.target.value)}
            className={
              "rounded-md border border-border-subtle bg-raised px-2 "
              + "py-1.5 text-sm"
            }
          />
        </label>
      </form>
    </Modal>
  );
}


// datetime-local wants "YYYY-MM-DDTHH:mm" in local tz. ``toISOString``
// would shift to UTC, which would surprise the producer when the
// browser tz != UTC.
function _localNowIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T`
    + `${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}
